import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const VBOT_CONNECT_URL = 'https://admin.shopify.com'; // TODO: replace with exact V-Bot Shopify app URL

export default function Integrations() {
  const { selectedBusiness, user } = useAuth();
  const bizId = selectedBusiness?.id;
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [shopifyDomain, setShopifyDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [notified, setNotified] = useState({});

  const [showSyncedData, setShowSyncedData] = useState(false);
  const [syncedRecords, setSyncedRecords] = useState([]);
  const [syncedLoading, setSyncedLoading] = useState(false);
  const [syncedError, setSyncedError] = useState('');
  const [syncedFilter, setSyncedFilter] = useState('all'); // all | order | customer
  const [waTemplates, setWaTemplates] = useState([]);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAll() {
    setLoading(true);
    try {
      const bizRes = await api.get('/businesses/my');
      setShopifyDomain(bizRes.data.shopify_domain || '');
      setApiKey(bizRes.data.api_key || '');
      setWebhookUrl(bizRes.data.webhook_url || '');
      const [statsRes, tmplRes] = await Promise.all([
        api.get('/shopify/stats'),
        api.get('/templates?business_id=' + bizRes.data.id),
      ]);
      setStats(statsRes.data || {});
      setWaTemplates((tmplRes.data || []).filter(t => (t.status || '').toUpperCase() === 'APPROVED'));
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function fetchSyncedData() {
    setSyncedLoading(true);
    setSyncedError('');
    try {
      const res = await api.get('/shopify/synced-records');
      setSyncedRecords(res.data?.records || res.data || []);
    } catch (e) {
      console.error(e);
      setSyncedError('Could not load synced data. Please try again.');
    }
    setSyncedLoading(false);
  }

  function toggleSyncedData() {
    const next = !showSyncedData;
    setShowSyncedData(next);
    if (next) fetchSyncedData();
  }

  function copyKey() {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function saveWebhook() {
    try {
      await api.post('/businesses/webhook', { webhook_url: webhookUrl });
      setWebhookSaved(true);
      setTimeout(() => setWebhookSaved(false), 2000);
    } catch (e) { console.error(e); }
  }

  function connectShopify() {
    navigate('/connect-shopify');
  }

  function notify(id) {
    setNotified(n => ({ ...n, [id]: true }));
  }

  async function disconnectShopify() {
    if (!window.confirm('Disconnect your Shopify store? Order and cart sync will stop.')) return;
    try {
      await api.post('/businesses/disconnect-shopify');
      setShopifyDomain('');
      setShowSyncedData(false);
      setSyncedRecords([]);
    } catch (e) { console.error(e); alert('Could not disconnect. Please try again.'); }
  }

  const s = {
    page: { padding: '28px 32px', fontFamily: 'DM Sans, sans-serif', background: '#f5f6fa', minHeight: '100vh' },
    heroCard: { textAlign: 'center', padding: '32px 24px 24px', background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', marginBottom: 20 },
    kicker: { fontSize: 12, fontWeight: 600, color: '#6366f1', marginBottom: 8 },
    heroTitle: { fontSize: 22, fontWeight: 700, color: '#111', margin: '0 0 8px' },
    heroSub: { fontSize: 13.5, color: '#6b7280', maxWidth: 420, margin: '0 auto' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 },
    card: (color) => ({ background: `linear-gradient(135deg, ${color}18, ${color}05)`, border: `1px solid ${color}55`, borderRadius: 14, padding: 16 }),
    cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
    icon: (color) => ({ width: 36, height: 36, borderRadius: 9, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }),
    badgeReady: { fontSize: 10, fontWeight: 600, background: '#dcfce7', color: '#166534', padding: '3px 9px', borderRadius: 10 },
    badgeSoon: { fontSize: 10, fontWeight: 600, background: '#f3f4f6', color: '#6b7280', padding: '3px 9px', borderRadius: 10 },
    cardName: { fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 3 },
    cardDesc: { fontSize: 12, color: '#6b7280', marginBottom: 12, minHeight: 32 },
    cardBtn: (bg) => ({ width: '100%', background: bg, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, padding: '8px', cursor: 'pointer', fontFamily: 'inherit' }),
    cardBtnGhost: { width: '100%', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, padding: '8px', cursor: 'pointer', fontFamily: 'inherit' },
    statsRow: { display: 'flex', gap: 12, marginBottom: 16 },
    statCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px', flex: 1 },
    statVal: { fontSize: 24, fontWeight: 700, color: '#111' },
    statLabel: { fontSize: 12, color: '#6b7280', marginTop: 4 },
    dataSection: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', marginBottom: 24 },
    dataSectionHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f3f4f6' },
    tabs: { display: 'flex', borderBottom: '1px solid #e5e7eb' },
    tab: (active) => ({ padding: '10px 16px', fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? '#166534' : '#6b7280', background: active ? '#f0fdf4' : '#fff', border: '1px solid ' + (active ? '#bbf7d0' : '#e5e7eb'), borderRadius: 8, cursor: 'pointer', marginRight: 6 }),
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 16px', textAlign: 'left', color: '#6b7280', fontWeight: 500, borderBottom: '1px solid #f3f4f6', background: '#fafafa' },
    td: { padding: '10px 16px', color: '#111', borderBottom: '1px solid #f3f4f6' },
    typeBadge: (type) => ({ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: type === 'Order' ? '#dbeafe' : '#f3e8ff', color: type === 'Order' ? '#1e40af' : '#6b21a8' }),
    emptyState: { padding: '32px 16px', textAlign: 'center', color: '#6b7280', fontSize: 13 },
    card2: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px', marginBottom: 16 },
    sectionTitle: { fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 4 },
    sectionSub: { fontSize: 12, color: '#6b7280', marginBottom: 16 },
    divider: { height: 1, background: '#f3f4f6', margin: '16px 0' },
    keyBox: { background: '#f8faff', border: '1.5px solid #e2e8f5', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
    keyVal: (visible) => ({ fontSize: 13, fontWeight: 600, color: '#1a1d2e', fontFamily: 'monospace', filter: visible ? 'none' : 'blur(5px)', transition: 'filter 0.3s' }),
    keyActions: { display: 'flex', gap: 8 },
    btnSm: (bg, color, border) => ({ padding: '6px 14px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: bg, color, border: border || 'none', fontFamily: 'inherit' }),
    urlInput: { width: '100%', padding: '10px 14px', borderRadius: 9, border: '1.5px solid #e2e8f5', background: '#f8faff', fontSize: 13, color: '#1a1d2e', fontFamily: 'inherit', outline: 'none' },
    endpointRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9, border: '1px solid #f0f2f8', background: '#fafbfd', marginBottom: 6 },
    method: (type) => ({ fontSize: 10, fontWeight: 800, borderRadius: 5, padding: '3px 8px', background: type === 'POST' ? '#f0fdf4' : '#eff6ff', color: type === 'POST' ? '#16a34a' : '#2563eb', minWidth: 40, textAlign: 'center' }),
    successCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '28px 20px', textAlign: 'center', marginBottom: 16 },
    successIcon: { width: 48, height: 48, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: 22 },
    successTitle: { margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#111' },
    successSub: { fontSize: 13, color: '#6b7280', marginBottom: 18 },
    successDetails: { background: '#f8faff', borderRadius: 10, padding: '12px 16px', maxWidth: 340, margin: '0 auto 18px', textAlign: 'left' },
    successRow: { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f0f2f8' },
    successRowLast: { display: 'flex', justifyContent: 'space-between', padding: '7px 0' },
    successLabel: { fontSize: 12.5, color: '#6b7280' },
    successVal: { fontSize: 12.5, fontWeight: 600, color: '#1a1d2e' },
    successBtns: { display: 'flex', gap: 8, maxWidth: 340, margin: '0 auto' },
  };

  const cards = [
    { id: 'shopify', color: '#95BF47', icon: '🛍️', name: 'Shopify', desc: shopifyDomain || 'Orders, carts, customers synced automatically.', ready: true },
    { id: 'custom', color: '#E4A026', icon: '🪝', name: 'Custom / API', desc: 'Use our REST API and webhooks.', ready: true },
    { id: 'metaads', color: '#1877F2', icon: '📢', name: 'Meta ads', desc: 'Click-to-WhatsApp leads land in your CRM.', ready: false },
    { id: 'woocommerce', color: '#96588A', icon: '🟣', name: 'WooCommerce', desc: 'Automatic sync for WordPress stores.', ready: false },
    { id: 'sheets', color: '#0F9D58', icon: '📊', name: 'Google Sheets', desc: 'Export contacts and reports automatically.', ready: false },
    { id: 'razorpay', color: '#072C65', icon: '💳', name: 'Razorpay', desc: 'Share payment links inside WhatsApp chats.', ready: false },
    { id: 'zoho', color: '#C8202F', icon: '🏢', name: 'Zoho CRM', desc: 'Two-way lead sync for existing Zoho teams.', ready: false },
    { id: 'tally', color: '#2874A6', icon: '🧮', name: 'Tally', desc: 'Export orders and invoices for accounting.', ready: false },
  ];

  const filteredRecords = syncedRecords.filter(r => syncedFilter === 'all' ? true : r.type?.toLowerCase().startsWith(syncedFilter));

  return (
    <div style={s.page}>
      <div style={s.heroCard}>
        <div style={s.kicker}>Integrations</div>
        <h2 style={s.heroTitle}>Connect VBOT to the tools you already use</h2>
        <p style={s.heroSub}>Pick a platform below to get started. Most connections take under 2 minutes.</p>
      </div>

      {shopifyDomain && (
        <div style={s.successCard}>
          <div style={s.successIcon}>✅</div>
          <h3 style={s.successTitle}>Shopify store connected</h3>
          <div style={s.successSub}>{shopifyDomain} is synced with VBOT</div>
          <div style={s.successDetails}>
            <div style={s.successRow}>
              <span style={s.successLabel}>Store</span>
              <span style={s.successVal}>{shopifyDomain}</span>
            </div>
            <div style={s.successRow}>
              <span style={s.successLabel}>Connected by</span>
              <span style={s.successVal}>{user?.name || 'You'}</span>
            </div>
            <div style={s.successRow}>
              <span style={s.successLabel}>Tenant ID</span>
              <span style={{ ...s.successVal, fontFamily: 'monospace' }}>{bizId}</span>
            </div>
            <div style={s.successRowLast}>
              <span style={s.successLabel}>Last synced</span>
              <span style={s.successVal}>{stats.lastSynced || 'Just now'}</span>
            </div>
          </div>
          <div style={s.successBtns}>
            <button style={s.cardBtnGhost} onClick={disconnectShopify}>Disconnect</button>
            <button style={s.cardBtn('#25D366')} onClick={toggleSyncedData}>
              {showSyncedData ? 'Hide synced data' : 'View synced data'}
            </button>
          </div>

        </div>
      )}

      {shopifyDomain && showSyncedData && (
        <div style={s.dataSection}>
          <div style={s.dataSectionHead}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Synced data</div>
            <div>
              <button style={s.tab(syncedFilter === 'all')} onClick={() => setSyncedFilter('all')}>All</button>
              <button style={s.tab(syncedFilter === 'order')} onClick={() => setSyncedFilter('order')}>Orders</button>
              <button style={s.tab(syncedFilter === 'customer')} onClick={() => setSyncedFilter('customer')}>Customers</button>
            </div>
          </div>

          {syncedLoading ? (
            <div style={s.emptyState}>Loading synced records…</div>
          ) : syncedError ? (
            <div style={s.emptyState}>{syncedError}</div>
          ) : filteredRecords.length === 0 ? (
            <div style={s.emptyState}>No synced records to show yet.</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Type</th>
                  <th style={s.th}>Record</th>
                  <th style={s.th}>Synced at</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((r, i) => (
                  <tr key={r.id || i}>
                    <td style={s.td}><span style={s.typeBadge(r.type)}>{r.type}</span></td>
                    <td style={s.td}>{r.label}</td>
                    <td style={s.td}>{r.syncedAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={s.grid}>
        {cards.filter(c => !(c.id === 'shopify' && shopifyDomain)).map(c => (
          <div key={c.id} style={s.card(c.color)}>
            <div style={s.cardTop}>
              <div style={s.icon(c.color)}>{c.icon}</div>
              <span style={c.ready ? s.badgeReady : s.badgeSoon}>{c.ready ? 'Ready' : 'Coming soon'}</span>
            </div>
            <div style={s.cardName}>{c.name}</div>
            <div style={s.cardDesc}>{c.desc}</div>
            {c.id === 'shopify' ? (
              shopifyDomain
                ? <div style={{ ...s.cardBtn('#dcfce7'), color: '#166534', textAlign: 'center', cursor: 'default' }}>✓ Connected</div>
                : <button style={s.cardBtn(c.color)} onClick={connectShopify}>Connect</button>
            ) : c.id === 'custom' ? (
              <button style={s.cardBtn(c.color)} onClick={() => setShowDocs(v => !v)}>{showDocs ? 'Hide docs' : 'View docs'}</button>
            ) : notified[c.id] ? (
              <button style={{ ...s.cardBtnGhost, color: '#16a34a' }} disabled>We'll notify you ✓</button>
            ) : (
              <button style={s.cardBtnGhost} onClick={() => notify(c.id)}>Notify me</button>
            )}
          </div>
        ))}
      </div>

      {/* ── API DOCS (Custom/API "View docs") ── */}
      {showDocs && (
        <div style={s.card2}>
          <div style={s.sectionTitle}>🔑 API Key</div>
          <div style={s.sectionSub}>Use this key to connect any external app to VBOT</div>

          <div style={s.keyBox}>
            <div>
              <div style={{ fontSize: 10, color: '#9098b5', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Your API Key</div>
              <div style={s.keyVal(keyVisible)}>{apiKey || 'vf_live_xxxxxxxxxxxxxxxxxxxxxxxx'}</div>
            </div>
            <div style={s.keyActions}>
              <button style={s.btnSm('#f1f3f9', '#555c7a', '1.5px solid #e2e8f5')} onClick={() => setKeyVisible(!keyVisible)}>
                {keyVisible ? '🙈 Hide' : '👁 Show'}
              </button>
              <button style={s.btnSm(copied ? '#16a34a' : '#1a1d2e', '#fff')} onClick={copyKey}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <div style={s.divider} />

          <div style={{ fontSize: 10, color: '#9098b5', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Webhook URL <span style={{ color: '#c0c8e0', fontWeight: 400, textTransform: 'none' }}>(optional — receive message status updates)</span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input style={s.urlInput} type="url" placeholder="https://yourapp.com/webhook"
              value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} />
            <button style={{ ...s.btnSm(webhookSaved ? '#16a34a' : '#22c55e', '#fff'), padding: '10px 20px', fontSize: 13, whiteSpace: 'nowrap' }} onClick={saveWebhook}>
              {webhookSaved ? 'Saved ✓' : 'Save'}
            </button>
          </div>

          <div style={s.divider} />

          <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 10 }}>API Endpoints</div>
          {[
            { method: 'POST', path: '/api/v1/send-message', desc: 'Send plain text message' },
            { method: 'POST', path: '/api/v1/send-template', desc: 'Send template message' },
            { method: 'POST', path: '/api/v1/contacts', desc: 'Add or update contact' },
            { method: 'GET',  path: '/api/v1/contacts', desc: 'List all contacts' },
            { method: 'GET',  path: '/api/v1/status/:id', desc: 'Check message status' },
          ].map((ep, i) => (
            <div key={i} style={s.endpointRow}>
              <span style={s.method(ep.method)}>{ep.method}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1d2e', fontFamily: 'monospace', flex: 1 }}>{ep.path}</span>
              <span style={{ fontSize: 11.5, color: '#9098b5' }}>{ep.desc}</span>
            </div>
          ))}

          <div style={s.divider} />

          <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 10 }}>Quick Example</div>
          <div style={{ background: '#1a1d2e', borderRadius: 10, padding: 16 }}>
            <pre style={{ fontSize: 11.5, color: '#e2e8f0', fontFamily: 'monospace', lineHeight: 1.7, margin: 0, overflow: 'auto' }}>{`fetch('https://vbot.co.in/api/v1/send-template', {
  method: 'POST',
  headers: {
    'X-API-Key': '${apiKey || 'vf_live_your_key'}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '919884328301',
    template: 'order_confirmation',
    params: ['John', '#1234', '₹599']
  })
})`}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
